import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { ApiError } from '../../../adapters/api-client/api';
import EmptyState from '../../../commodity/components/ui/EmptyState';
import ErrorState from '../../../commodity/components/ui/ErrorState';
import LoadingState from '../../../commodity/components/ui/LoadingState';
import { RootState } from '../../../store/store';
import {
  getPastClasses,
  PastClass,
  PastClassAttendance,
  PastClassOutcome,
  PastClassRecordingState,
  PlaybackLink,
  RecordingRefusalReason,
  requestRecordingAccess,
} from '../api/learning';
import {
  getMyReviews,
  getPendingReviews,
  OwnReview,
  PendingReview,
  ReviewSubject,
  submitReviews,
} from '../api/reviews';
import { getStudents, StudentListItem } from '../api/students';
import type { DisplayTimeZone } from '../api/learner';
import { familyZoneLabel, formatLongDate } from '../time/zonedTime';
import { useLearnerTime } from '../time/useFamilyTime';
import PlaybackPermission from '../components/PlaybackPermission';

// Times in the learner's zone as the server chose it (UC-P08 BR-P08-04).
const fmtDate = (iso: string | undefined, tz: DisplayTimeZone): string =>
  formatLongDate(iso, tz, true);

const fmtDay = (iso: string | undefined, tz: DisplayTimeZone): string => formatLongDate(iso, tz);

export const ATTENDANCE_LABEL: Record<PastClassAttendance, string> = {
  PRESENT: 'Asistió',
  LATE: 'Llegó tarde',
  NO_SHOW: 'No asistió',
  UNKNOWN: 'Asistencia sin registrar',
};

/** A class that was not held, as what it was (DEC-S3-P10-01). */
const NOT_HELD_LABEL: Partial<Record<PastClassOutcome, string>> = {
  CANCELLED: 'Cancelada',
  RESCHEDULED: 'Reprogramada',
  NOT_DELIVERED: 'No se dio',
};

/** What each recording state means to a family, plainly (PRT-13-FR-04). */
export const RECORDING_COPY: Record<PastClassRecordingState, string> = {
  AVAILABLE: 'Grabación disponible.',
  PROCESSING: 'La grabación se está procesando. Vuelve a revisar más tarde.',
  NOT_RELEASED: 'La grabación todavía no está publicada para las familias.',
  RESTRICTED: 'Esta grabación no está disponible.',
  MEDIA_UNAVAILABLE: 'Las grabaciones no están disponibles por ahora.',
  EXPIRED: 'La grabación ya no está disponible: pasó su plazo de disponibilidad.',
  NOT_ENTITLED: 'Solo hay grabación de las clases a las que tu alumno asistió.',
  PLAYBACK_NOT_GRANTED:
    'No has autorizado ver grabaciones de este alumno. Cuando lo autorices, podrás verlas aquí.',
  NOT_RECORDED: 'Esta clase no tiene grabación.',
  FAILED: 'La grabación de esta clase falló y no está disponible.',
};

/** Why a "Ver grabación" click was refused, in the family's words. */
export const REFUSAL_COPY: Record<RecordingRefusalReason, string> = {
  NOT_OWNER: 'Esta grabación no pertenece a ninguno de tus alumnos.',
  NOT_ATTENDED: RECORDING_COPY.NOT_ENTITLED,
  PLAYBACK_NOT_GRANTED: RECORDING_COPY.PLAYBACK_NOT_GRANTED,
  NOT_READY: RECORDING_COPY.PROCESSING,
  NOT_RELEASED: RECORDING_COPY.NOT_RELEASED,
  RESTRICTED: RECORDING_COPY.RESTRICTED,
  MEDIA_UNAVAILABLE: RECORDING_COPY.MEDIA_UNAVAILABLE,
  EXPIRED: RECORDING_COPY.EXPIRED,
  NOT_RECORDED: RECORDING_COPY.NOT_RECORDED,
};

const SUBJECT_LABEL: Record<ReviewSubject, string> = {
  TEACHER: 'al profesor',
  COURSE: 'el curso',
};

const rowKey = (c: Pick<PastClass, 'enrollmentId' | 'seq'>) => `${c.enrollmentId}#${c.seq}`;

const refusalOf = (error: unknown): RecordingRefusalReason | undefined => {
  if (!(error instanceof ApiError)) return undefined;
  const reason = error.body?.reason;
  return typeof reason === 'string' && reason in REFUSAL_COPY
    ? (reason as RecordingRefusalReason)
    : undefined;
};

const card: React.CSSProperties = {
  border: '1px solid #d1d5da',
  borderRadius: 10,
  padding: 14,
  background: '#fff',
};

/**
 * The family's past classes, one learner at a time (UC-P10).
 *
 * A projection of what the server knows: only classes that ended, newest
 * first, with the attendance on record and an honest account of the
 * recording. "Ver grabación" exists only on a row the server says is
 * available, and each click asks for a new link — the link lives minutes and
 * is never kept. The player is told not to offer a download.
 */
const PastClasses: React.FC = () => {
  const activeLearnerId = useSelector((s: RootState) => s.context.activeContext?.studentId ?? null);
  // A link from the Agenda names the event's learner in router state, never
  // in the address (AC-11, AC-25).
  const location = useLocation();
  const routedLearnerId = (location.state as { learnerId?: unknown } | null)?.learnerId;

  const [students, setStudents] = useState<StudentListItem[] | null>(null);
  const [studentsError, setStudentsError] = useState<unknown>(null);
  const [studentId, setStudentId] = useState<string | null>(null);

  const [classes, setClasses] = useState<PastClass[] | null>(null);
  const [classesError, setClassesError] = useState<unknown>(null);
  const [historyIncomplete, setHistoryIncomplete] = useState(false);
  /** The learner the newest request is for: a slower reply for another is dropped. */
  const requestedFor = useRef<string | null>(null);
  const { tz } = useLearnerTime(studentId);

  const loadStudents = useCallback(async () => {
    setStudentsError(null);
    try {
      const list = await getStudents();
      setStudents(list ?? []);
    } catch (err) {
      setStudentsError(err);
    }
  }, []);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  // The learner the link was for, when it named one of this family's;
  // otherwise the learner the parent is acting as; otherwise the first.
  // Chosen once the list is known, never guessed before it.
  useEffect(() => {
    if (!students || students.length === 0) return;
    setStudentId((current) => {
      if (current && students.some((s) => s.studentId === current)) return current;
      const routed = students.find((s) => s.studentId === routedLearnerId);
      const active = students.find((s) => s.studentId === activeLearnerId);
      return (routed ?? active ?? students[0]).studentId;
    });
  }, [students, activeLearnerId, routedLearnerId]);

  const loadClasses = useCallback(async (id: string) => {
    requestedFor.current = id;
    setClasses(null);
    setClassesError(null);
    setHistoryIncomplete(false);
    try {
      const result = await getPastClasses(id);
      // DEC-S3-P08-01: a late reply for learner A must not render under learner B.
      if (requestedFor.current !== id) return;
      setClasses(result.classes ?? []);
      setHistoryIncomplete(Boolean(result.historyIncomplete));
    } catch (err) {
      if (requestedFor.current === id) setClassesError(err);
    }
  }, []);

  useEffect(() => {
    if (studentId) loadClasses(studentId);
  }, [studentId, loadClasses]);

  if (studentsError) {
    return (
      <Frame>
        <ErrorState action="cargar tus alumnos" error={studentsError} onRetry={loadStudents} />
      </Frame>
    );
  }
  if (students === null) {
    return (
      <Frame>
        <LoadingState what="tus alumnos" />
      </Frame>
    );
  }
  if (students.length === 0) {
    return (
      <Frame>
        <EmptyState
          message="Todavía no tienes alumnos registrados."
          detail="Las clases pasadas aparecen aquí cuando tu alumno termina su primera clase."
          action={{ label: 'Agregar alumno', to: '/parent/students/new' }}
        />
      </Frame>
    );
  }

  return (
    <Frame>
      {students.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <label htmlFor="past-classes-learner" style={{ display: 'block', marginBottom: 4 }}>
            Alumno
          </label>
          <select
            id="past-classes-learner"
            value={studentId ?? ''}
            onChange={(e) => setStudentId(e.target.value)}
          >
            {students.map((s) => (
              <option key={s.studentId} value={s.studentId}>
                {`${s.firstName} ${s.lastName}`.trim()}
              </option>
            ))}
          </select>
        </div>
      )}

      {classesError ? (
        <ErrorState
          action="cargar las clases pasadas"
          error={classesError}
          onRetry={() => studentId && loadClasses(studentId)}
        />
      ) : classes === null || !studentId ? (
        <LoadingState what="las clases pasadas" />
      ) : classes.length === 0 ? (
        <EmptyState
          message="Este alumno todavía no tiene clases pasadas."
          detail="Cuando termine una clase, aparecerá aquí con su asistencia y su grabación."
        />
      ) : (
        <>
          <p style={{ color: '#666', marginTop: 0, fontSize: 13 }}>{familyZoneLabel(tz)}</p>
          {historyIncomplete && (
            <p role="status" style={{ fontSize: 14 }}>
              Por ahora no pudimos cargar las clases canceladas o reprogramadas.
            </p>
          )}
          <PlaybackPermission
            studentId={studentId}
            learnerName={students.find((s) => s.studentId === studentId)?.firstName || 'tu alumno'}
            needsGrant={classes.some((c) => c.recording.state === 'PLAYBACK_NOT_GRANTED')}
            onChanged={() => loadClasses(studentId)}
          />
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
            {classes.map((c) => (
              <PastClassRow key={rowKey(c)} item={c} studentId={studentId} tz={tz} />
            ))}
          </ul>
          <ReviewPanel classes={classes} tz={tz} />
        </>
      )}
    </Frame>
  );
};

const Frame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ maxWidth: 760, margin: '0 auto' }}>
    <h1 style={{ marginBottom: 4 }}>Clases pasadas</h1>
    <p style={{ color: '#666', marginTop: 0 }}>
      Las clases que ya terminaron, con la asistencia y la grabación de cada una.
    </p>
    {children}
  </div>
);

/** One past class, its recording state, and the player when opened. */
const PastClassRow: React.FC<{ item: PastClass; studentId: string; tz: DisplayTimeZone }> = ({
  item,
  studentId,
  tz,
}) => {
  const [link, setLink] = useState<PlaybackLink | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [linkLapsed, setLinkLapsed] = useState(false);
  const resumeAt = useRef(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  const play = async () => {
    setRequesting(true);
    setError(null);
    setLinkLapsed(false);
    try {
      // A new link for every click: the last one may already have expired,
      // and none is ever stored (MVP-07).
      setLink(await requestRecordingAccess(item.enrollmentId, item.seq, studentId));
    } catch (err) {
      setLink(null);
      setError(err);
    } finally {
      setRequesting(false);
    }
  };

  const onVideoError = () => {
    // A short-lived link can lapse mid-class. Say so and offer a fresh one
    // from where the learner was, rather than a broken player.
    resumeAt.current = videoRef.current?.currentTime ?? 0;
    setLinkLapsed(true);
  };

  const onLoaded = () => {
    if (videoRef.current && resumeAt.current > 0) {
      videoRef.current.currentTime = resumeAt.current;
    }
  };

  const refusal = refusalOf(error);
  const { state, availableUntil } = item.recording;
  const notHeld = NOT_HELD_LABEL[item.outcome];
  const titleId = `past-class-${rowKey(item)}`;

  return (
    <li style={card} aria-labelledby={titleId}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div id={titleId} style={{ fontWeight: 700 }}>
            {item.courseTitle} · Sesión {item.seq}
          </div>
          <div style={{ fontSize: 14, color: '#555' }}>
            {fmtDate(item.scheduledStart ?? item.sessionEndedAt, tz)}
            {item.teacherName ? ` · Profesor: ${item.teacherName}` : ''}
          </div>
        </div>
        <span
          style={{
            alignSelf: 'flex-start',
            fontSize: 12,
            fontWeight: 700,
            borderRadius: 4,
            padding: '3px 8px',
            background: item.attendance === 'NO_SHOW' ? '#ffe9e9' : '#eef1f5',
            color: item.attendance === 'NO_SHOW' ? '#8f2323' : '#334',
          }}
        >
          {notHeld ?? ATTENDANCE_LABEL[item.attendance] ?? ATTENDANCE_LABEL.UNKNOWN}
        </span>
      </div>

      {item.suggestedPractice && (
        // MC-02 — the teacher's optional suggestion, read-only. Nothing to hand
        // in, no due date, no grade (MC02-R03).
        <section
          aria-label={`Práctica sugerida de la sesión ${item.seq}`}
          style={{ margin: '10px 0 0', fontSize: 14 }}
        >
          <strong>Práctica sugerida (opcional): </strong>
          {item.suggestedPractice.text}
          <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
            {item.suggestedPractice.estimatedMinutes
              ? `Unos ${item.suggestedPractice.estimatedMinutes} minutos. `
              : ''}
            No se entrega ni se califica; tampoco afecta al curso.
          </div>
        </section>
      )}

      {item.outcome === 'PARTIAL' && (
        <p style={{ margin: '10px 0 0', fontSize: 14 }}>Esta clase duró menos de lo previsto.</p>
      )}

      <p style={{ margin: '10px 0 0', fontSize: 14 }}>
        {state === 'AVAILABLE' && availableUntil
          ? `Grabación disponible hasta el ${fmtDay(availableUntil, tz)}.`
          : RECORDING_COPY[state] ?? RECORDING_COPY.NOT_RECORDED}
      </p>

      <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
        {state === 'AVAILABLE' && (
          <button type="button" onClick={play} disabled={requesting}>
            {requesting ? 'Preparando…' : link ? 'Volver a cargar' : 'Ver grabación'}
          </button>
        )}
        {link && (
          <button type="button" onClick={() => setLink(null)}>
            Cerrar grabación
          </button>
        )}
        <Link to={`/enrollments/${item.enrollmentId}`}>Ver detalle de la inscripción</Link>
      </div>

      {error ? (
        refusal ? (
          <p role="alert" style={{ margin: '10px 0 0', color: '#8f2323', fontSize: 14 }}>
            {REFUSAL_COPY[refusal]}
          </p>
        ) : (
          <div style={{ marginTop: 10 }}>
            <ErrorState action="abrir la grabación" error={error} onRetry={play} compact />
          </div>
        )
      ) : null}

      {link && !linkLapsed && (
        <div style={{ marginTop: 12 }}>
          {/* Played in the page, never offered as a download (MVP-07). The
              recordings carry no captions track yet. */}
          <video
            ref={videoRef}
            key={link.url}
            src={link.url}
            controls
            controlsList="nodownload noremoteplayback"
            disablePictureInPicture
            preload="metadata"
            onContextMenu={(e) => e.preventDefault()}
            onError={onVideoError}
            onLoadedMetadata={onLoaded}
            aria-label={`Grabación de ${item.courseTitle}, sesión ${item.seq}`}
            style={{ width: '100%', borderRadius: 8, background: '#000' }}
          />
        </div>
      )}

      {link && linkLapsed && (
        <div role="status" style={{ marginTop: 10, fontSize: 14 }}>
          El enlace de la grabación venció por seguridad.{' '}
          <button type="button" onClick={play} disabled={requesting}>
            Seguir viendo
          </button>
        </div>
      )}
    </li>
  );
};

/**
 * Rate the class (PRT-13-FR-05/06). Offered only for what the server says is
 * still owed; what was already written is shown as written. The window and the
 * attendance rule are the server's — nothing here decides eligibility.
 */
const ReviewPanel: React.FC<{ classes: PastClass[]; tz: DisplayTimeZone }> = ({ classes, tz }) => {
  const [pending, setPending] = useState<PendingReview[] | null>(null);
  const [mine, setMine] = useState<OwnReview[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [p, m] = await Promise.all([getPendingReviews(), getMyReviews()]);
      setPending(p.pending ?? []);
      setMine(m.reviews ?? []);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const enrollments = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of classes) if (!seen.has(c.enrollmentId)) seen.set(c.enrollmentId, c.courseTitle);
    return Array.from(seen.entries());
  }, [classes]);

  if (error) {
    return (
      <section style={{ marginTop: 20 }}>
        <ErrorState action="cargar tus calificaciones" error={error} onRetry={load} compact />
      </section>
    );
  }
  if (pending === null || mine === null) return <LoadingState what="tus calificaciones" compact />;

  const groups = enrollments.flatMap(([enrollmentId, courseTitle]) => {
    const dimensions = (['TEACHER', 'COURSE'] as ReviewSubject[]).flatMap((subject) => {
      const written = mine.find((r) => r.transactionId === enrollmentId && r.subject === subject);
      const owed = pending.find((p) => p.transactionId === enrollmentId && p.subject === subject);
      if (!written && !owed) return [];
      return [{ subject, written, owed }];
    });
    return dimensions.length > 0 ? [{ enrollmentId, courseTitle, dimensions }] : [];
  });
  if (groups.length === 0) return null;

  return (
    <section aria-labelledby="past-classes-reviews" style={{ marginTop: 24 }}>
      <h2 id="past-classes-reviews" style={{ fontSize: 18, marginBottom: 8 }}>
        Califica tus clases
      </h2>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
        {groups.map((group) => (
          <li key={group.enrollmentId} style={card}>
            <ReviewGroup {...group} tz={tz} onSent={load} />
          </li>
        ))}
      </ul>
    </section>
  );
};

/** What a family is told about a review they wrote (DEC-S3-P10-10/11). */
const reviewStateCopy = (review: OwnReview): string => {
  if (review.underReview) return 'En revisión: se publica cuando la aprobemos.';
  return review.status === 'VISIBLE' ? 'Publicada.' : 'No publicada.';
};

interface ReviewDimensionRow {
  subject: ReviewSubject;
  written?: OwnReview;
  owed?: PendingReview;
}

/** Draft values per dimension; an empty rating leaves the dimension out of the submit. */
type Drafts = Partial<Record<ReviewSubject, { rating: string; comment: string }>>;

/**
 * One enrollment's reviews. What is owed is rated in one form, and sent as one
 * set: the course, the teacher, or both, which persist together or not at all
 * (DEC-S3-P10-08). A written review can be edited while the window is open, and
 * the edit goes back to review (DEC-S3-P10-11).
 */
const ReviewGroup: React.FC<{
  enrollmentId: string;
  courseTitle: string;
  dimensions: ReviewDimensionRow[];
  tz: DisplayTimeZone;
  onSent: () => Promise<void>;
}> = ({ enrollmentId, courseTitle, dimensions, tz, onSent }) => {
  const [editing, setEditing] = useState<ReviewSubject | null>(null);
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState<Drafts>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const owed = dimensions.filter((d) => d.owed && !d.written);
  const forms = open ? owed : dimensions.filter((d) => d.subject === editing);
  const until = owed.find((d) => d.owed?.eligibleUntil)?.owed?.eligibleUntil;

  const draftOf = (d: ReviewDimensionRow) =>
    drafts[d.subject] ?? {
      rating: d.written ? String(d.written.rating) : '',
      comment: d.written?.comment ?? '',
    };
  const setDraft = (d: ReviewDimensionRow, change: { rating?: string; comment?: string }) =>
    setDrafts((prev) => ({ ...prev, [d.subject]: { ...draftOf(d), ...change } }));

  const chosen = forms.filter((d) => draftOf(d).rating !== '');

  const send = async () => {
    setSaving(true);
    setError(null);
    try {
      await submitReviews({
        transactionId: enrollmentId,
        dimensions: chosen.map((d) => ({
          subject: d.subject,
          rating: Number(draftOf(d).rating),
          comment: draftOf(d).comment.trim() || undefined,
          ...(d.written ? { expectedVersion: d.written.version } : {}),
        })),
      });
      setOpen(false);
      setEditing(null);
      setDrafts({});
      // The server decides what is now written; read it back.
      await onSent();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {dimensions
        .filter((d) => d.written)
        .map(({ subject, written }) => (
          <div
            key={subject}
            style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}
          >
            <span>
              Ya calificaste {SUBJECT_LABEL[subject]} de {courseTitle}: {written?.rating} de 5.{' '}
              {written ? reviewStateCopy(written) : ''}
            </span>
            {written?.editableUntil ? (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setEditing(editing === subject ? null : subject);
                }}
              >
                {editing === subject ? 'Cancelar' : `Editar ${SUBJECT_LABEL[subject]}`}
              </button>
            ) : null}
          </div>
        ))}

      {owed.length > 0 ? (
        <div
          style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}
        >
          <span>
            Califica {courseTitle}
            {until ? ` (hasta el ${fmtDay(until, tz)})` : ''}
          </span>
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setOpen(!open);
            }}
          >
            {open ? 'Cancelar' : 'Calificar'}
          </button>
        </div>
      ) : null}

      {forms.length > 0 ? (
        <div style={{ marginTop: 10 }}>
          {forms.map((d) => {
            const id = `${enrollmentId}-${d.subject}`;
            return (
              <div key={d.subject} style={{ marginBottom: 10 }}>
                <label htmlFor={`rating-${id}`} style={{ display: 'block', marginBottom: 4 }}>
                  Estrellas para {SUBJECT_LABEL[d.subject]}
                </label>
                <select
                  id={`rating-${id}`}
                  value={draftOf(d).rating}
                  onChange={(e) => setDraft(d, { rating: e.target.value })}
                >
                  <option value="">Sin calificar</option>
                  {[5, 4, 3, 2, 1].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <label htmlFor={`comment-${id}`} style={{ display: 'block', margin: '8px 0 4px' }}>
                  Comentario sobre {SUBJECT_LABEL[d.subject]} (opcional)
                </label>
                <textarea
                  id={`comment-${id}`}
                  value={draftOf(d).comment}
                  onChange={(e) => setDraft(d, { comment: e.target.value })}
                  rows={3}
                  style={{ width: '100%' }}
                />
              </div>
            );
          })}
          {error ? <ErrorState action="enviar tu calificación" error={error} compact /> : null}
          <button type="button" onClick={send} disabled={saving || chosen.length === 0}>
            {saving ? 'Enviando…' : 'Enviar calificación'}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default PastClasses;
